/** The ONLY fields that cross the I/O boundary (subscription-inventory.ts drops
 *  everything else az returns — names, systemData.createdBy, tags — right here in
 *  its .map). Defined in the pure module so the import graph stays one-directional. */
export interface InventoryRow {
  type: string;
  resourceGroup: string;
  location: string;
  createdTime: string | null;
}

/** The notable-resource count at or above which a subscription counts as "heavy"
 *  and the intake persona opens the rundown. This is the number to tune once a
 *  real resource-heavy subscription shows whether the moment lands; exported and
 *  rendered in tests so changing it is a one-line edit with a guarding test. */
export const HEAVY_THRESHOLD = 10;

/** The exact sentence the intake persona branches on — it must match the sentence
 *  in the persona byte for byte, or the rundown silently never fires.
 *  Present in every heavy note, absent from every light/absent note. */
export const INVENTORY_INVITATION = "You can offer to walk the founder through these resources.";

/**
 * Normalized-lowercase ARM type → the plural display noun used in the note.
 *
 * The whitelist IS the trust boundary for type vocabulary: only a type in this map
 * counts toward the threshold and can be named. Azure-auto NOISE (network watchers,
 * alert rules/action groups/autoscale, Log Analytics + App Insights plumbing, managed
 * identities, container-app managed environments, cognitive …/projects sub-resources)
 * is excluded BY ABSENCE — it is simply not here.
 *
 * Several types can share a noun (databases, messaging namespaces, networking
 * resources) so a family reads as one figure instead of fragmenting.
 */
export const NOTABLE_TYPES: Readonly<Record<string, string>> = {
  // storage
  "microsoft.storage/storageaccounts": "storage accounts",
  // compute
  "microsoft.compute/virtualmachines": "virtual machines",
  "microsoft.compute/virtualmachinescalesets": "virtual machine scale sets",
  "microsoft.app/containerapps": "container apps",
  "microsoft.containerservice/managedclusters": "Kubernetes clusters",
  "microsoft.web/sites": "web apps",
  // data (shared noun)
  "microsoft.sql/servers": "databases",
  "microsoft.dbforpostgresql/servers": "databases",
  "microsoft.dbforpostgresql/flexibleservers": "databases",
  "microsoft.dbformysql/servers": "databases",
  "microsoft.dbformysql/flexibleservers": "databases",
  "microsoft.documentdb/databaseaccounts": "databases",
  "microsoft.cache/redis": "databases",
  // AI (shared noun)
  "microsoft.cognitiveservices/accounts": "AI services",
  "microsoft.machinelearningservices/workspaces": "AI services",
  // key vaults
  "microsoft.keyvault/vaults": "key vaults",
  // container registries
  "microsoft.containerregistry/registries": "container registries",
  // messaging (shared noun)
  "microsoft.servicebus/namespaces": "messaging namespaces",
  "microsoft.eventhub/namespaces": "messaging namespaces",
  // networking (shared noun); public DNS zones only — private zones are plumbing
  "microsoft.network/virtualnetworks": "networking resources",
  "microsoft.network/loadbalancers": "networking resources",
  "microsoft.network/applicationgateways": "networking resources",
  "microsoft.network/dnszones": "networking resources",
};

export type Verdict = "heavy" | "light";

export interface InventorySummary {
  verdict: Verdict;
  notableCount: number;
  /** Display-noun groups, descending by count (ties broken alphabetically). */
  categories: { noun: string; count: number }[];
  /** The region holding a majority of notable rows, else null. */
  topRegion: string | null;
  regionSpread: number;
  /** Fixed age-bucket phrase for the oldest notable resource, or null if unknown. */
  oldestBucket: string | null;
}

const MS_PER_DAY = 86_400_000;
const REGION_MAJORITY = 0.6;

/** Ordered oldest-resource age buckets. Vague-but-true; never an exact date. */
const AGE_BUCKETS: readonly { maxDays: number; phrase: string }[] = [
  { maxDays: 31, phrase: "all created within the last month" },
  { maxDays: 93, phrase: "the oldest created a couple of months ago" },
  { maxDays: 186, phrase: "the oldest created several months ago" },
  { maxDays: 365, phrase: "the oldest created about a year ago" },
  { maxDays: 730, phrase: "the oldest created over a year ago" },
];
const OLDEST_FALLBACK = "the oldest created a couple of years ago";

function ageBucket(days: number): string {
  for (const b of AGE_BUCKETS) if (days <= b.maxDays) return b.phrase;
  return OLDEST_FALLBACK;
}

/**
 * Reduce a subscription's resources to a conversation-ready summary.
 *
 * Excludes the install resource group (exact, case-insensitive) and any
 * non-notable type, then derives verdict, per-noun counts, the dominant region,
 * and the oldest age bucket. Pure — the clock is injected as `now` (ms).
 */
export function summarizeInventory(
  rows: InventoryRow[], installResourceGroup: string, now: number,
): InventorySummary {
  const installRg = installResourceGroup.toLowerCase();
  const notable = rows.filter(
    (r) => r.resourceGroup.toLowerCase() !== installRg
      && Object.hasOwn(NOTABLE_TYPES, r.type.toLowerCase()),
  );
  const notableCount = notable.length;

  const byNoun = new Map<string, number>();
  for (const r of notable) {
    const noun = NOTABLE_TYPES[r.type.toLowerCase()];
    byNoun.set(noun, (byNoun.get(noun) ?? 0) + 1);
  }
  const categories = [...byNoun.entries()]
    .map(([noun, count]) => ({ noun, count }))
    .sort((a, b) => b.count - a.count || a.noun.localeCompare(b.noun));

  const byRegion = new Map<string, number>();
  for (const r of notable) {
    if (!r.location) continue;
    const loc = r.location.toLowerCase();
    byRegion.set(loc, (byRegion.get(loc) ?? 0) + 1);
  }
  const regionSpread = byRegion.size;
  let topRegion: string | null = null;
  for (const [region, count] of byRegion) {
    if (notableCount > 0 && count / notableCount >= REGION_MAJORITY) { topRegion = region; break; }
  }

  let oldestMs: number | null = null;
  for (const r of notable) {
    if (!r.createdTime) continue;
    const t = Date.parse(r.createdTime);
    if (Number.isNaN(t)) continue;
    if (oldestMs === null || t < oldestMs) oldestMs = t;
  }
  const oldestBucket = oldestMs === null ? null : ageBucket((now - oldestMs) / MS_PER_DAY);

  return {
    verdict: notableCount >= HEAVY_THRESHOLD ? "heavy" : "light",
    notableCount, categories, topRegion, regionSpread, oldestBucket,
  };
}

const LEAD = "I looked over what's already in this subscription, outside the resources m8t is setting up.";
const MAX_NOUNS = 4;

/** Common Azure region enums → friendly display; falls back to the raw enum. */
const REGION_NAMES: Readonly<Record<string, string>> = {
  eastus: "East US", eastus2: "East US 2", westus: "West US", westus2: "West US 2",
  westus3: "West US 3", centralus: "Central US", southcentralus: "South Central US",
  westeurope: "West Europe", northeurope: "North Europe", uksouth: "UK South",
  swedencentral: "Sweden Central", australiaeast: "Australia East",
  eastasia: "East Asia", southeastasia: "Southeast Asia",
};

function regionDisplay(region: string): string {
  return REGION_NAMES[region.toLowerCase()] ?? region;
}

/** Plain-English list: "a" / "a and b" / "a, b and c". */
function joinList(items: string[]): string {
  if (items.length <= 1) return items[0] ?? "";
  return `${items.slice(0, -1).join(", ")} and ${items[items.length - 1]}`;
}

function piecesClause(s: InventorySummary): string {
  const shown = s.categories.slice(0, MAX_NOUNS).map((c) => `${String(c.count)} ${c.noun}`);
  if (s.categories.length > MAX_NOUNS) {
    return `The notable pieces: ${shown.join(", ")}, and a handful of others`;
  }
  return `The notable pieces: ${joinList(shown)}`;
}

function regionAgeClause(s: InventorySummary): string {
  const region = s.topRegion
    ? `mostly in ${regionDisplay(s.topRegion)}`
    : s.regionSpread > 1 ? `spread across ${String(s.regionSpread)} regions` : "";
  const parts = [region, s.oldestBucket ?? ""].filter((p) => p !== "");
  return parts.length > 0 ? ` — ${parts.join(", ")}` : "";
}

/**
 * Render the persona-facing note. Trusted vocabulary only.
 *
 * heavy → summary + " " + INVENTORY_INVITATION.
 * light → summary, NO invitation.
 * The persona branches on the invitation's presence, so it must appear IFF heavy.
 */
export function renderInventoryNote(summary: InventorySummary): string {
  const body = summary.notableCount === 0
    ? `${LEAD} There are no notable pre-existing resources.`
    : `${LEAD} ${piecesClause(summary)}${regionAgeClause(summary)}.`;
  return summary.verdict === "heavy" ? `${body} ${INVENTORY_INVITATION}` : body;
}
